// screens/marketplace/CourseDetail.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ChevronLeft,
  GraduationCap,
  ShieldCheck,
  CreditCard,
  Wallet,
  AlertCircle,
  Check,
  Lock,
  User,
  Mail,
  Phone,
  Send,
} from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useTheme } from "../../hooks/useTheme";
import { useToast } from "../../providers/ToastProvider";
import RichText from "./RichText";
import { getCourse, enrollInCourse } from "./coursesApi";

const money = (v) => `₵${Number(v || 0).toFixed(2)}`;

const PAYMENT_METHODS = [
  {
    value: "PAYSTACK",
    icon: CreditCard,
    title: "Card or Mobile Money",
    subtitle: "Secured by Paystack",
  },
  {
    value: "WALLET",
    icon: Wallet,
    title: "Azoemart Wallet",
    subtitle: "Instant checkout from balance",
  },
];

const initialForm = () => ({
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  telegram_username: "",
  payment_method: "PAYSTACK",
});

export default function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authFetch, isAuthenticated, user } = useAuth();
  const { colors } = useTheme();
  const { showToast } = useToast();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(initialForm());
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Prefill contact name/email from the signed-in user, once.
  useEffect(() => {
    if (!user) return;
    setForm((prev) => ({
      ...prev,
      contact_name: prev.contact_name || user.full_name || user.username || "",
      contact_email: prev.contact_email || user.email || "",
      contact_phone: prev.contact_phone || user.phone_number || "",
    }));
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCourse(authFetch, id);
      setCourse(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, id]);

  useEffect(() => {
    load();
  }, [load]);

  const price = course?.effective_price || course?.price;

  // Field-level validation for the enrollment form.
  const formErrors = useMemo(() => {
    const errs = {};
    if (!form.contact_name.trim()) errs.contact_name = "Your name is required";
    if (!form.contact_email.trim()) errs.contact_email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(form.contact_email))
      errs.contact_email = "Enter a valid email";
    if (!form.contact_phone.trim()) errs.contact_phone = "Phone is required";
    else if (form.contact_phone.replace(/\D/g, "").length < 9)
      errs.contact_phone = "Enter a valid phone number";
    if (!form.telegram_username.trim())
      errs.telegram_username = "Telegram username is required";
    else if (!/^@?[a-zA-Z0-9_]{3,}$/.test(form.telegram_username.trim()))
      errs.telegram_username = "Use your Telegram @username";
    return errs;
  }, [form]);

  const canEnroll =
    !submitting && Object.keys(formErrors).length === 0 && !!course;

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (touched[key]) setTouched((t) => ({ ...t, [key]: false }));
  };

  const submit = async (e) => {
    e.preventDefault();

    // Force all fields to show errors on submit
    setTouched({
      contact_name: true,
      contact_email: true,
      contact_phone: true,
      telegram_username: true,
    });

    if (Object.keys(formErrors).length > 0) {
      showToast("Please fix the highlighted fields", "error");
      return;
    }
    if (!isAuthenticated) {
      navigate(`/login?next=/courses/${id}`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        contact_name: form.contact_name.trim(),
        contact_email: form.contact_email.trim(),
        contact_phone: form.contact_phone.trim(),
        telegram_username: form.telegram_username.trim().startsWith("@")
          ? form.telegram_username.trim()
          : `@${form.telegram_username.trim()}`,
        payment_method: form.payment_method,
      };
      const data = await enrollInCourse(authFetch, id, payload);

      if (form.payment_method === "PAYSTACK" && data?.authorization_url) {
        window.location.assign(data.authorization_url);
        return;
      }

      // Wallet success path
      showToast("Enrolled successfully", "success");
      navigate(`/courses/enrollments/${data.id}?just_paid=1`);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Loading / error ──────────────────────────────── */
  if (loading) {
    return (
      <div style={styles.pageWrap}>
        <div style={styles.loadingWrap(colors)}>
          <div style={styles.spinner(colors)} />
          <span style={{ color: colors.secondaryText, fontSize: 13 }}>
            Loading course…
          </span>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div style={styles.pageWrap}>
        <button
          type="button"
          onClick={() => navigate("/courses")}
          style={styles.backBtn(colors)}
        >
          <ChevronLeft size={16} /> All courses
        </button>
        <div style={styles.errorWrap(colors)}>
          <div style={styles.errorIcon(colors)}>
            <AlertCircle size={26} />
          </div>
          <h2 style={styles.errorTitle(colors)}>Course not available</h2>
          <p style={styles.errorText(colors)}>
            {error || "This course may have been removed."}
          </p>
          <button
            type="button"
            onClick={() => navigate("/courses")}
            style={styles.primaryBtn(colors)}
          >
            Back to courses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageWrap}>
      <button
        type="button"
        onClick={() => navigate("/courses")}
        style={styles.backBtn(colors)}
      >
        <ChevronLeft size={16} /> All courses
      </button>

      <div style={styles.layout} className="cd-layout">
        {/* ── Left: hero + description ───────────────── */}
        <div style={styles.leftCol}>
          <div style={styles.hero(colors)}>
            {course.cover_image_url ? (
              <img
                src={course.cover_image_url}
                alt={course.title}
                style={styles.heroImg}
              />
            ) : (
              <div style={styles.heroFallback(colors)}>
                <GraduationCap size={48} />
              </div>
            )}
            <span style={styles.heroBadge(colors)}>
              <GraduationCap size={10} />
              Digital course
            </span>
          </div>

          <h1 style={styles.title(colors)}>{course.title}</h1>
          {course.short_description && (
            <p style={styles.subtitle(colors)}>{course.short_description}</p>
          )}

          <section style={styles.section(colors)}>
            <h2 style={styles.sectionTitle(colors)}>About this course</h2>
            <RichText
              html={course.description}
              emptyLabel="No description provided yet."
            />
          </section>

          <section style={styles.section(colors)}>
            <h2 style={styles.sectionTitle(colors)}>What you get</h2>
            <ul style={styles.benefitsList}>
              <Bullet colors={colors}>Lifetime access after purchase</Bullet>
              <Bullet colors={colors}>Support via the Telegram community</Bullet>
              <Bullet colors={colors}>Works on mobile and desktop</Bullet>
            </ul>
          </section>
        </div>

        {/* ── Right: enrollment card ──────────────────── */}
        <aside style={styles.rightCol}>
          <div style={styles.enrollCard(colors)}>
            <div style={styles.enrollPrice(colors)}>{money(price)}</div>
            <div style={styles.enrollPriceSub(colors)}>
              One-time payment. No subscription.
            </div>

            <form onSubmit={submit} style={{ marginTop: 18 }}>
              <div style={styles.formGrid}>
                <EnrollField
                  icon={User}
                  label="Full name"
                  value={form.contact_name}
                  onChange={(v) => updateField("contact_name", v)}
                  onBlur={() =>
                    setTouched((t) => ({ ...t, contact_name: true }))
                  }
                  error={touched.contact_name ? formErrors.contact_name : null}
                  colors={colors}
                />
                <EnrollField
                  icon={Mail}
                  label="Email"
                  type="email"
                  value={form.contact_email}
                  onChange={(v) => updateField("contact_email", v)}
                  onBlur={() =>
                    setTouched((t) => ({ ...t, contact_email: true }))
                  }
                  error={touched.contact_email ? formErrors.contact_email : null}
                  colors={colors}
                />
                <EnrollField
                  icon={Phone}
                  label="Phone number"
                  type="tel"
                  value={form.contact_phone}
                  onChange={(v) => updateField("contact_phone", v)}
                  onBlur={() =>
                    setTouched((t) => ({ ...t, contact_phone: true }))
                  }
                  error={touched.contact_phone ? formErrors.contact_phone : null}
                  colors={colors}
                />
                <EnrollField
                  icon={Send}
                  label="Telegram username"
                  placeholder="@yourhandle"
                  value={form.telegram_username}
                  onChange={(v) => updateField("telegram_username", v)}
                  onBlur={() =>
                    setTouched((t) => ({ ...t, telegram_username: true }))
                  }
                  error={
                    touched.telegram_username
                      ? formErrors.telegram_username
                      : null
                  }
                  colors={colors}
                  hint="We'll use this to accept your Telegram join request."
                />
              </div>

              <div style={styles.payMethodLabel(colors)}>Payment method</div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
              >
                {PAYMENT_METHODS.map((m) => {
                  const Icon = m.icon;
                  const active = form.payment_method === m.value;
                  return (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => updateField("payment_method", m.value)}
                      style={styles.payBtn(colors, active)}
                    >
                      <span
                        style={styles.payIcon(colors, active)}
                        aria-hidden
                      >
                        <Icon size={16} />
                      </span>
                      <span style={{ flex: 1, textAlign: "left" }}>
                        <span
                          style={{
                            display: "block",
                            fontSize: 13.5,
                            fontWeight: 700,
                            color: colors.heading,
                          }}
                        >
                          {m.title}
                        </span>
                        <span
                          style={{
                            display: "block",
                            fontSize: 11.5,
                            color: colors.secondaryText,
                            marginTop: 2,
                          }}
                        >
                          {m.subtitle}
                        </span>
                      </span>
                      <span style={styles.radio(colors, active)}>
                        {active && <span style={styles.radioDot(colors)} />}
                      </span>
                    </button>
                  );
                })}
              </div>

              <button
                type="submit"
                disabled={!canEnroll}
                style={styles.enrollCta(colors, canEnroll)}
              >
                {submitting ? (
                  <>
                    <span style={styles.spinnerInline(colors)} />
                    Processing…
                  </>
                ) : (
                  <>
                    {isAuthenticated ? "Enroll" : "Sign in to enroll"} ·{" "}
                    {money(price)}
                  </>
                )}
              </button>

              <div style={styles.reassure(colors)}>
                <ShieldCheck size={12} />
                Secure payment · Telegram invite after payment
              </div>
            </form>
          </div>

          {/* Reassurance card */}
          <div style={styles.infoCard(colors)}>
            <div style={styles.infoRow}>
              <Lock size={14} color={colors.btnBg} />
              <span>
                <strong>One-time purchase.</strong> Yours forever, no renewals.
              </span>
            </div>
            <div style={styles.infoRow}>
              <Send size={14} color={colors.btnBg} />
              <span>
                <strong>Telegram invite</strong> appears here after payment
                confirms.
              </span>
            </div>
          </div>
        </aside>
      </div>

      <style>{`
        @media (min-width: 900px) {
          .cd-layout {
            grid-template-columns: minmax(0, 1.55fr) minmax(320px, 1fr) !important;
          }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Bits
   ══════════════════════════════════════════════════════════ */

const Bullet = ({ colors, children }) => (
  <li
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      fontSize: 14,
      lineHeight: 1.6,
      color: colors.text,
      marginBottom: 8,
    }}
  >
    <Check
      size={15}
      color={colors.success}
      style={{ flexShrink: 0, marginTop: 3 }}
    />
    <span>{children}</span>
  </li>
);

const EnrollField = ({
  icon: Icon,
  label,
  type = "text",
  value,
  onChange,
  onBlur,
  error,
  hint,
  placeholder,
  colors,
}) => (
  <div>
    <label
      style={{
        display: "block",
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: 0.3,
        textTransform: "uppercase",
        color: colors.secondaryText,
        marginBottom: 6,
      }}
    >
      {label}
    </label>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "0 10px",
        borderRadius: 10,
        border: `1px solid ${error ? colors.error : colors.border}`,
        background: colors.raisedBg,
        boxShadow: error ? `0 0 0 3px ${colors.errorBg}` : "none",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        height: 42,
      }}
    >
      <Icon size={14} color={colors.secondaryText} />
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        style={{
          flex: 1,
          minWidth: 0,
          border: "none",
          background: "transparent",
          color: colors.text,
          fontSize: 13.5,
          outline: "none",
          fontFamily: "inherit",
        }}
      />
    </div>
    {error ? (
      <div
        style={{
          marginTop: 4,
          fontSize: 11.5,
          color: colors.error,
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <AlertCircle size={11} />
        {error}
      </div>
    ) : hint ? (
      <div
        style={{
          marginTop: 4,
          fontSize: 11,
          color: colors.secondaryText,
          lineHeight: 1.4,
        }}
      >
        {hint}
      </div>
    ) : null}
  </div>
);

/* ══════════════════════════════════════════════════════════
   Styles
   ══════════════════════════════════════════════════════════ */

const styles = {
  pageWrap: {
    maxWidth: 1180,
    margin: "0 auto",
    padding: "16px 16px 64px",
  },
  backBtn: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: "transparent",
    border: "none",
    color: c.secondaryText,
    cursor: "pointer",
    padding: "6px 0",
    marginBottom: 14,
    fontSize: 13,
    fontWeight: 600,
  }),

  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 28,
  },
  leftCol: { minWidth: 0 },
  rightCol: { minWidth: 0 },

  hero: (c) => ({
    position: "relative",
    aspectRatio: "16 / 9",
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${c.border}`,
    background: c.hoverBg,
    marginBottom: 20,
  }),
  heroImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  heroFallback: (c) => ({
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: c.secondaryText,
    background: `linear-gradient(135deg, ${c.hoverBg}, ${c.subtleBg || c.hoverBg})`,
  }),
  heroBadge: (c) => ({
    position: "absolute",
    top: 14,
    left: 14,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "5px 11px",
    borderRadius: 999,
    background: c.raisedBg,
    color: c.btnBg,
    fontSize: 10.5,
    fontWeight: 800,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    boxShadow: `0 2px 8px ${c.shadow}`,
  }),

  title: (c) => ({
    margin: 0,
    fontSize: "clamp(22px, 3.4vw, 30px)",
    fontWeight: 800,
    letterSpacing: "-0.02em",
    lineHeight: 1.15,
    color: c.heading,
  }),
  subtitle: (c) => ({
    margin: "8px 0 0",
    fontSize: 15,
    color: c.secondaryText,
    lineHeight: 1.55,
  }),

  section: (c) => ({
    marginTop: 22,
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    borderRadius: 16,
    padding: 20,
  }),
  sectionTitle: (c) => ({
    margin: "0 0 12px",
    fontSize: 15,
    fontWeight: 800,
    color: c.heading,
    letterSpacing: "-0.01em",
  }),
  benefitsList: {
    margin: 0,
    padding: 0,
    listStyle: "none",
  },

  enrollCard: (c) => ({
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    borderRadius: 16,
    padding: 20,
    position: "sticky",
    top: 84,
  }),
  enrollPrice: (c) => ({
    fontSize: 30,
    fontWeight: 800,
    color: c.heading,
    letterSpacing: "-0.02em",
  }),
  enrollPriceSub: (c) => ({
    marginTop: 4,
    fontSize: 12.5,
    color: c.secondaryText,
  }),
  formGrid: {
    display: "grid",
    gap: 14,
  },
  payMethodLabel: (c) => ({
    margin: "20px 0 10px",
    fontSize: 11.5,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    color: c.secondaryText,
  }),
  payBtn: (c, active) => ({
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    padding: 12,
    borderRadius: 12,
    border: `1.5px solid ${active ? c.btnBg : c.border}`,
    background: active ? c.hoverBg : "transparent",
    cursor: "pointer",
    transition: "border-color 0.15s ease, background 0.15s ease",
  }),
  payIcon: (c, active) => ({
    width: 32,
    height: 32,
    borderRadius: 9,
    background: active ? c.btnBg : c.subtleBg || c.hoverBg,
    color: active ? c.btnText : c.btnBg,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "background 0.15s ease, color 0.15s ease",
  }),
  radio: (c, active) => ({
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: `1.5px solid ${active ? c.btnBg : c.border}`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
  radioDot: (c) => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: c.btnBg,
  }),
  enrollCta: (c, enabled) => ({
    width: "100%",
    marginTop: 18,
    padding: "13px 18px",
    borderRadius: 12,
    border: "none",
    background: enabled ? c.btnBg : c.subtleBg || c.hoverBg,
    color: enabled ? c.btnText : c.secondaryText,
    fontSize: 14.5,
    fontWeight: 700,
    cursor: enabled ? "pointer" : "not-allowed",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    boxShadow: enabled ? `0 4px 14px ${c.btnBg}33` : "none",
  }),
  reassure: (c) => ({
    marginTop: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 11.5,
    color: c.secondaryText,
  }),
  spinnerInline: (c) => ({
    width: 14,
    height: 14,
    border: `2px solid ${c.btnText}55`,
    borderTopColor: c.btnText,
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  }),

  infoCard: (c) => ({
    marginTop: 14,
    background: c.subtleBg || c.hoverBg,
    border: `1px solid ${c.border}`,
    borderRadius: 14,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  }),
  infoRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    fontSize: 12.5,
    lineHeight: 1.5,
  },

  loadingWrap: (c) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 320,
    gap: 12,
  }),
  spinner: (c) => ({
    width: 32,
    height: 32,
    border: `3px solid ${c.border}`,
    borderTopColor: c.btnBg,
    borderRadius: "50%",
    animation: "spin 0.9s linear infinite",
  }),
  errorWrap: (c) => ({
    maxWidth: 380,
    margin: "60px auto",
    textAlign: "center",
    padding: 24,
  }),
  errorIcon: (c) => ({
    width: 64,
    height: 64,
    borderRadius: 20,
    background: c.hoverBg,
    color: c.secondaryText,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  }),
  errorTitle: (c) => ({
    margin: "0 0 6px",
    fontSize: 18,
    fontWeight: 800,
    color: c.heading,
  }),
  errorText: (c) => ({
    margin: "0 0 22px",
    fontSize: 13.5,
    color: c.secondaryText,
    lineHeight: 1.55,
  }),
  primaryBtn: (c) => ({
    padding: "11px 20px",
    borderRadius: 10,
    border: "none",
    background: c.btnBg,
    color: c.btnText,
    fontWeight: 700,
    fontSize: 13.5,
    cursor: "pointer",
  }),
};